import React, { useMemo } from "react";
import { useGame } from "@/context/GameContext";
import { RIVAL, WEEK_LENGTH } from "@/data/gameData";
import Avatar from "@/components/avatar/Avatar";
import { Trophy } from "lucide-react";

const Bar = ({ label, value, max, color, testid }) => (
  <div>
    <div className="flex justify-between text-xs font-heading font-black text-slate-700 mb-1">
      <span>{label}</span>
      <span data-testid={testid}>${value}</span>
    </div>
    <div className="h-4 bg-white rounded-full border-2 border-slate-200 overflow-hidden">
      <div className={`h-full ${color} transition-all duration-700`} style={{ width: `${max ? Math.max(4, (value / max) * 100) : 4}%` }} />
    </div>
  </div>
);

const RivalCard = () => {
  const { state } = useGame();
  const dayInWeek = ((state.day - 1) % WEEK_LENGTH) + 1;
  const max = Math.max(state.weekEarnings, state.rival.weekEarnings, 1);
  const leading = state.weekEarnings > state.rival.weekEarnings;
  const quip = useMemo(() => RIVAL.quips[(state.day + state.rival.stolen) % RIVAL.quips.length], [state.day, state.rival.stolen]);

  return (
    <div className="bg-orange-50 rounded-3xl border-4 border-orange-300 p-5 card-3d" data-testid="rival-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="bg-white rounded-2xl border-2 border-orange-300 p-1 shrink-0">
          <Avatar avatar={RIVAL.avatar} size={64} testid="rival-avatar" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] font-black uppercase tracking-widest text-orange-700">Your rival</div>
          <h3 className="font-display text-xl font-bold text-slate-900 leading-tight">
            {RIVAL.name} · <span data-testid="rival-rep">★{state.rival.reputation.toFixed(1)}</span>
            {state.rival.prevReputation != null && state.rival.prevReputation !== state.rival.reputation && (
              <span className={`ml-1 text-sm ${state.rival.reputation > state.rival.prevReputation ? "text-emerald-600" : "text-rose-600"}`} data-testid="rival-rep-trend">
                {state.rival.reputation > state.rival.prevReputation ? "▲" : "▼"} since yesterday
              </span>
            )}
          </h3>
          <p className="text-xs font-bold text-slate-600 italic mt-1" data-testid="rival-quip">“{quip}”</p>
        </div>
      </div>

      <div className="bg-white/70 rounded-2xl border-2 border-orange-200 p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-xs font-black uppercase tracking-wider text-slate-600">
            🏁 Top Boss race · Week {state.week} · Day {dayInWeek}/{WEEK_LENGTH}
          </div>
          <div className="flex items-center gap-1 text-xs font-black text-amber-700" data-testid="trophy-count">
            <Trophy className="w-4 h-4" /> {state.trophies}
          </div>
        </div>
        <Bar label={`You (★${state.reputation.toFixed(1)})`} value={state.weekEarnings} max={max} color="bg-emerald-400" testid="race-you" />
        <Bar label={RIVAL.name} value={state.rival.weekEarnings} max={max} color="bg-orange-400" testid="race-rival" />
        <div className="text-xs font-heading font-bold text-slate-700" data-testid="race-status">
          {leading ? "🎉 You're in the lead! Keep your prices fair and your stars high." : "😬 Riley is ahead. Earn more profit before the week ends!"}
          {state.rival.weekStolen > 0 && ` Riley grabbed ${state.rival.weekStolen} of your customers this week.`}
        </div>
      </div>
    </div>
  );
};

export default RivalCard;
